import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { frontendURL, cbePartsCatalogUrl, requestsApiURL } from '../config';
import useDebounce from '../hooks/useDebounce';
import LoadingAnimation from './LoadingAnimation';

const PAGE_LIMIT = 20;

const qInputCls = "w-full border border-[#D9DDE6] px-3 py-2.5 focus:outline-none focus:border-[#131F38]";

const QField = ({ label, children }) => (
  <div>
    <label className="block text-xs font-bold tracking-wide text-[#3D4A61] mb-1">{label}</label>
    {children}
  </div>
);

const QSectionLabel = ({ children }) => (
  <div className="text-[11px] font-bold tracking-wide text-[#5B6679] uppercase pt-1 border-t border-[#E8EBF1]">
    {children}
  </div>
);

const PartThumb = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" className="w-14 h-14 text-[#AEB7C6]">
    <rect x="2.5" y="4.5" width="19" height="15" />
    <path d="M2.5 16l5-5 4 4 3-3 7 6" />
    <circle cx="8.5" cy="9" r="1.6" />
  </svg>
);

const Thumb = ({ src, alt }) => {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return <PartThumb />;
  return (
    <img
      src={src}
      alt={alt || ''}
      onError={() => setBroken(true)}
      className="w-full h-full object-contain"
    />
  );
};

const PartCard = ({ part, qty, onQtyChange, onAdd }) => {
  const label = part.partDescription && part.partDescription.trim() ? part.partDescription : 'Unnamed part';
  return (
    <article className="border border-[#D9DDE6] bg-white flex flex-col">
      <div className="aspect-square bg-[#F4F5F8] border-b border-[#D9DDE6] flex items-center justify-center overflow-hidden">
        <Thumb src={part.image} alt={label} />
      </div>
      <div className="p-3.5 flex flex-col gap-2 flex-1">
        <h4 className="text-[14.5px] font-bold leading-snug text-[#131F38]">{label}</h4>
        {part.partNumber ? (
          <div className="text-[12.5px] text-[#5B6679] font-mono">{part.partNumber}</div>
        ) : (
          <div className="text-[12.5px] text-[#93A0B5] italic">No part number assigned</div>
        )}
        <div className="flex items-stretch border border-[#D9DDE6] mt-auto h-9">
          <button
            type="button"
            onClick={() => onQtyChange(-1)}
            aria-label={`Decrease quantity for ${part.partNumber || label}`}
            className="w-9 flex-none flex items-center justify-center leading-none hover:bg-[#F4F5F8] font-bold text-[#131F38]"
          >
            &minus;
          </button>
          <input
            type="text"
            inputMode="numeric"
            readOnly
            value={qty}
            aria-label={`Quantity for ${part.partNumber || label}`}
            className="flex-1 w-0 text-center border-x border-[#D9DDE6] leading-none"
          />
          <button
            type="button"
            onClick={() => onQtyChange(1)}
            aria-label={`Increase quantity for ${part.partNumber || label}`}
            className="w-9 flex-none flex items-center justify-center leading-none hover:bg-[#F4F5F8] font-bold text-[#131F38]"
          >
            +
          </button>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="bg-[#C9A227] hover:bg-[#A8871B] text-[#0B1526] font-bold text-sm py-2"
        >
          Add to quote
        </button>
      </div>
    </article>
  );
};

const AssemblyThumb = ({ src, alt }) => {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return null;
  return (
    <div className="w-12 h-12 flex-none bg-[#F4F5F8] border border-[#D9DDE6] flex items-center justify-center overflow-hidden">
      <img
        src={src}
        alt={alt || ''}
        onError={() => setBroken(true)}
        className="w-full h-full object-contain"
      />
    </div>
  );
};

const AssemblySection = ({ assembly, qtyMap, onQtyChange, onAdd, defaultOpen }) => {
  const [isOpen, setIsOpen] = useState(!!defaultOpen);

  return (
    <section className="mb-6 border border-[#D9DDE6] bg-white">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="w-full flex items-center gap-3 p-4 bg-[#131F38] border-b border-[#D9DDE6] text-left"
      >
        <AssemblyThumb src={assembly.image} alt={assembly.assemblyName} />
        <div className="flex-1">
          <h3 className="inline-block text-base font-bold text-gray-300 px-2 py-0.5 m-0">
            {assembly.assemblyName}
          </h3>
          {assembly.assemblyNumber ? (
            <div className="text-xs text-gray-300 font-mono mt-1 px-2">{assembly.assemblyNumber}</div>
          ) : (
            <div className="text-xs text-gray-300 italic mt-1">No assembly number</div>
          )}
        </div>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`w-5 h-5 flex-none text-gray-300 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        assembly.parts && assembly.parts.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
            {assembly.parts.map((part) => (
              <PartCard
                key={part.id}
                part={part}
                qty={qtyMap[part.id] || 1}
                onQtyChange={(delta) => onQtyChange(part.id, delta)}
                onAdd={() => onAdd(part)}
              />
            ))}
          </div>
        ) : (
          <div className="text-sm text-[#5B6679] italic p-4">No parts listed under this assembly.</div>
        )
      )}
    </section>
  );
};

function EqidPartsStore({ serialNumber, modsId }) {
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  // useDebounce (not useSearchDebounce) deliberately — that hook's reset-to-page-1 effect keys
  // off `currentPage`, which here also advances from infinite scroll; feeding it our `page`
  // caused every scroll-triggered page bump to re-arm an 800ms timer that forced page back to 1,
  // fighting the scroll loader and flooding the API with requests. Debouncing just the search
  // text, and resetting the page ourselves only when the debounced text truly changes, avoids that.
  const debouncedSearch = useDebounce(searchInput);
  const [appliedSearch, setAppliedSearch] = useState('');

  useEffect(() => {
    setAppliedSearch(debouncedSearch.trim());
    setPage(1);
  }, [debouncedSearch]);

  const [assemblies, setAssemblies] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [retryTick, setRetryTick] = useState(0);

  const [qtyMap, setQtyMap] = useState({});
  const [cart, setCart] = useState([]);
  const [quoteForm, setQuoteForm] = useState({
    name: '', company: '', email: '', phone: '',
    city: '', state: '', country: '',
    notes: '',
  });
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [quoteRef, setQuoteRef] = useState('');
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteError, setQuoteError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function fetchParts() {
      // Waits on modsId rather than fetching unscoped — the catalog is filtered
      // server-side to this machine's "Parts Pages" assemblies (see mods_id in
      // cbePartsController.js on EQID-Admin-Backend), so there's nothing correct
      // to show until it arrives.
      if (!modsId) return;

      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setFetchError('');

      try {
        const response = await axios.get(cbePartsCatalogUrl, {
          params: {
            mods_id: modsId,
            page,
            limit: PAGE_LIMIT,
            ...(appliedSearch ? { search: appliedSearch } : {}),
          },
        });
        if (cancelled) return;

        const newAssemblies = response.data?.data || [];
        setAssemblies((prev) => {
          if (page === 1) return newAssemblies;
          const seen = new Set(prev.map((a) => a.id));
          return [...prev, ...newAssemblies.filter((a) => !seen.has(a.id))];
        });
        setPagination(response.data?.pagination || null);
      } catch (err) {
        if (cancelled) return;
        setFetchError(
          err.response?.status === 401
            ? 'Unable to load the parts catalog right now. Please try again shortly.'
            : "Couldn't load parts. Please check your connection and try again."
        );
        if (page === 1) {
          setAssemblies([]);
          setPagination(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    }

    fetchParts();
    return () => {
      cancelled = true;
    };
  }, [appliedSearch, page, retryTick, modsId]);

  // Read via refs (not effect/callback deps) so the observer isn't torn down and recreated on
  // every loading/pagination change — doing that made a fresh observer re-fire immediately
  // whenever the sentinel was still on-screen, cascading through dozens of pages with no user
  // interaction. The sentinel <div> itself only exists once the content branch renders (not
  // during the loading/error/empty branches), so a plain mount-time effect would miss it —
  // a callback ref fires exactly when that div mounts/unmounts instead.
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(loadingMore);
  const paginationRef = useRef(pagination);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  const sentinelObserverRef = useRef(null);
  const sentinelRef = useCallback((node) => {
    if (sentinelObserverRef.current) {
      sentinelObserverRef.current.disconnect();
      sentinelObserverRef.current = null;
    }
    if (node) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            !loadingRef.current &&
            !loadingMoreRef.current &&
            paginationRef.current?.hasNextPage
          ) {
            setPage((p) => p + 1);
          }
        },
        { rootMargin: '200px' }
      );
      observer.observe(node);
      sentinelObserverRef.current = observer;
    }
  }, []);

  const resultCount = pagination ? pagination.total : assemblies.length;

  const getQty = (id) => qtyMap[id] || 1;
  const changeQty = (id, delta) => {
    setQtyMap((prev) => ({ ...prev, [id]: Math.max(1, (prev[id] || 1) + delta) }));
  };

  const addToCart = (part) => {
    const qty = getQty(part.id);
    const label = part.partDescription && part.partDescription.trim() ? part.partDescription : 'Unnamed part';
    setCart((prev) => {
      const existing = prev.find((c) => c.id === part.id);
      if (existing) {
        return prev.map((c) => (c.id === part.id ? { ...c, qty: c.qty + qty } : c));
      }
      return [...prev, { id: part.id, no: part.partNumber || '', name: label, qty }];
    });
  };

  const removeFromCart = (id) => setCart((prev) => prev.filter((c) => c.id !== id));
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  const handleQuoteFieldChange = (field) => (event) => {
    setQuoteForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleQuoteSubmit = async () => {
    if (quoteSubmitting) return;
    if (!cart.length) {
      setQuoteError('Add at least one part to your quote request first.');
      return;
    }

    const name = quoteForm.name.trim();
    const email = quoteForm.email.trim();
    if (!name || !email) {
      setQuoteError('Please enter your name and email so we can reply.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setQuoteError('Please enter a valid email address.');
      return;
    }

    setQuoteError('');
    setQuoteSubmitting(true);

    try {
      // Posts straight to findthebagger-mocks (not our own Mods backend — see
      // requestsApiURL in config.js) so CBE parts leads land on its "CBE Parts"
      // admin tab, the same place every other CBE parts quote request goes.
      const [firstName, ...rest] = name.split(/\s+/);
      const lastName = rest.join(' ');
      const eqidLink = serialNumber ? `${frontendURL}/eqid/${serialNumber}` : `${frontendURL}/find_eqid`;
      const notes = quoteForm.notes.trim();

      const response = await axios.post(`${requestsApiURL}/submit-quote-request`, {
        firstName,
        lastName,
        email,
        phone: quoteForm.phone.trim(),
        company: quoteForm.company.trim(),
        city: quoteForm.city.trim(),
        state: quoteForm.state.trim(),
        country: quoteForm.country.trim(),
        note: `EQID: ${serialNumber || 'unknown'} (${eqidLink})${notes ? `\n\n${notes}` : ''}`,
        parts: cart.map((c) => ({
          partNumber: c.no || '',
          partDescription: c.name,
          quantity: c.qty,
        })),
      });

      setQuoteRef(response.data?.leadId || '');
      setQuoteSubmitted(true);
    } catch (err) {
      setQuoteError(err.response?.data?.error || 'Could not send your request. Please try again.');
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const resetQuote = () => {
    setCart([]);
    setQtyMap({});
    setQuoteForm({
      name: '', company: '', email: '', phone: '',
      city: '', state: '', country: '',
      notes: '',
    });
    setQuoteSubmitted(false);
    setQuoteRef('');
    setQuoteError('');
  };

  const trimmedQuery = useMemo(() => searchInput.trim(), [searchInput]);
  const showEmptyState = !loading && !fetchError && assemblies.length === 0;

  return (
    <section className="bg-white border border-[#D9DDE6]">
      <div className="bg-[#131F38] text-white px-5 md:px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold m-0">Parts Store</h2>
          <p className="text-[#A9B4C7] text-[13px] mt-1 m-0">
            Know the part? Search by part number or name, then add it straight to your quote.
          </p>
        </div>
      </div>

      <div className="p-4 md:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start">
          <div>
            <div className="border border-[#D9DDE6] bg-[#F4F5F8] p-4 mb-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label htmlFor="partQuery" className="block text-xs font-bold tracking-wide text-[#3D4A61] mb-1.5">
                    PART NUMBER OR NAME
                  </label>
                  <input
                    id="partQuery"
                    type="search"
                    autoComplete="off"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="e.g. bag clamp cylinder, 804691, fill spout"
                    className="w-full border border-[#D9DDE6] bg-white px-3.5 py-3 focus:outline-none focus:border-[#131F38]"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => {
                      setPage(1);
                      setAppliedSearch(searchInput.trim());
                    }}
                    className="w-full sm:w-auto bg-[#C9A227] hover:bg-[#A8871B] text-[#0B1526] font-bold px-4 py-3 whitespace-nowrap"
                  >
                    Search parts
                  </button>
                </div>
              </div>
              <p className="text-[13px] text-[#5B6679] mt-3">
                Searches our digitized parts index. Can't find it? Our specialists can identify parts from a photo or sample.
              </p>
            </div>

            {trimmedQuery && (
              <div className="flex flex-wrap items-center gap-2.5 mt-5 mb-3.5 text-[13px]">
                <b className="text-base text-[#131F38]">
                  {`Matches for "${trimmedQuery}"`}
                </b>
                {!loading && !fetchError && (
                  <span className="text-xs font-bold text-[#166C46] bg-[#E7F3EC] border border-[#C9E2D5] px-2.5 py-1">
                    {resultCount} {resultCount === 1 ? 'assembly' : 'assemblies'}
                  </span>
                )}
              </div>
            )}

            {loading ? (
              <div className="border border-[#D9DDE6] bg-[#F4F5F8] py-10">
                <LoadingAnimation loadingText="Loading parts" />
              </div>
            ) : fetchError ? (
              <div className="border border-[#D9DDE6] bg-[#F4F5F8] p-7 text-center">
                <h4 className="font-bold text-[#131F38] mb-1.5">{fetchError}</h4>
                <button
                  type="button"
                  onClick={() => setRetryTick((t) => t + 1)}
                  className="inline-block bg-[#C9A227] hover:bg-[#A8871B] text-[#0B1526] font-bold px-4 py-3 mt-2"
                >
                  Retry
                </button>
              </div>
            ) : showEmptyState ? (
              <div className="border border-[#D9DDE6] bg-[#F4F5F8] p-7 text-center">
                <h4 className="font-bold text-[#131F38] mb-1.5">
                  {trimmedQuery ? `No parts matched "${trimmedQuery}"` : 'No parts found'}
                </h4>
                <p className="text-sm text-[#5B6679] mb-4">
                  Older and legacy machines aren't all in the index yet. Send us a photo, a drawing number or the
                  machine serial and a specialist will identify it.
                </p>
                <a
                  href="mailto:parts@choicebagging.com?subject=Part%20identification%20request"
                  className="inline-block bg-[#C9A227] hover:bg-[#A8871B] text-[#0B1526] font-bold px-4 py-3"
                >
                  Email a photo or part number
                </a>
              </div>
            ) : (
              <>
                {assemblies.map((assembly, index) => (
                  <AssemblySection
                    key={assembly.id}
                    assembly={assembly}
                    qtyMap={qtyMap}
                    onQtyChange={changeQty}
                    onAdd={addToCart}
                    defaultOpen={index === 0}
                  />
                ))}
                <div ref={sentinelRef} />
                {loadingMore && (
                  <div className="py-4">
                    <LoadingAnimation loadingText="Loading more" />
                  </div>
                )}
              </>
            )}
          </div>

          <aside className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto flex flex-col gap-4">
            <div className="border border-[#D9DDE6]">
              <div className="bg-[#0B1526] text-white px-4 py-4">
                <b className="block text-base">Quote Request</b>
                <span className="block text-[12.5px] text-[#A9B4C7] mt-1">
                  No payment required — we reply with pricing &amp; lead time
                </span>
              </div>

              <div className="max-h-72 overflow-auto divide-y divide-[#E8EBF1]">
                {cart.length === 0 ? (
                  <div className="px-4 py-5 text-[13.5px] text-[#5B6679]">
                    No parts added yet. Search above, or send us a photo and we'll identify the part for you.
                  </div>
                ) : (
                  cart.map((c) => (
                    <div key={c.id} className="px-4 py-3.5 grid grid-cols-[1fr_auto_auto] gap-2 items-start">
                      <div>
                        <b className="block text-[13.5px] font-bold leading-snug text-[#131F38]">{c.name}</b>
                        {c.no && <div className="text-xs text-[#5B6679] font-mono mt-0.5">{c.no}</div>}
                      </div>
                      <div className="text-[13px] font-bold text-[#131F38]">&times;{c.qty}</div>
                      <button
                        type="button"
                        onClick={() => removeFromCart(c.id)}
                        aria-label={`Remove ${c.no || c.name}`}
                        className="text-[#93A0B5] hover:text-[#9B2C2C] text-base leading-none px-0.5"
                      >
                        &times;
                      </button>
                    </div>
                  ))
                )}
              </div>

              {!quoteSubmitted ? (
                <>
                  <div className="p-4 grid gap-3 border-t border-[#E8EBF1]">
                    <QField label="Name">
                      <input
                        type="text"
                        value={quoteForm.name}
                        onChange={handleQuoteFieldChange('name')}
                        placeholder="Who should we reply to?"
                        className={qInputCls}
                      />
                    </QField>
                    <QField label="Company">
                      <input
                        type="text"
                        value={quoteForm.company}
                        onChange={handleQuoteFieldChange('company')}
                        placeholder="Company name"
                        className={qInputCls}
                      />
                    </QField>
                    <QField label="Email">
                      <input
                        type="email"
                        value={quoteForm.email}
                        onChange={handleQuoteFieldChange('email')}
                        placeholder="name@company.com"
                        className={qInputCls}
                      />
                    </QField>
                    <QField label="Phone (optional)">
                      <input
                        type="tel"
                        value={quoteForm.phone}
                        onChange={handleQuoteFieldChange('phone')}
                        placeholder="Phone number"
                        className={qInputCls}
                      />
                    </QField>

                    <QSectionLabel>Location (optional)</QSectionLabel>
                    <QField label="City">
                      <input
                        type="text"
                        value={quoteForm.city}
                        onChange={handleQuoteFieldChange('city')}
                        placeholder="City"
                        className={qInputCls}
                      />
                    </QField>
                    <QField label="State">
                      <input
                        type="text"
                        value={quoteForm.state}
                        onChange={handleQuoteFieldChange('state')}
                        placeholder="State"
                        className={qInputCls}
                      />
                    </QField>
                    <QField label="Country">
                      <input
                        type="text"
                        value={quoteForm.country}
                        onChange={handleQuoteFieldChange('country')}
                        placeholder="Country"
                        className={qInputCls}
                      />
                    </QField>

                    <QField label="Notes (optional)">
                      <textarea
                        value={quoteForm.notes}
                        onChange={handleQuoteFieldChange('notes')}
                        placeholder={serialNumber ? `Anything we should know — urgency, serial ${serialNumber}…` : 'Anything we should know — urgency, serial number…'}
                        className={`${qInputCls} min-h-[70px]`}
                      />
                    </QField>
                  </div>
                  <div className="px-4 pb-4 text-center">
                    {quoteError && (
                      <div className="text-sm text-[#9B2C2C] mb-2.5 text-left">{quoteError}</div>
                    )}
                    <button
                      type="button"
                      onClick={handleQuoteSubmit}
                      disabled={quoteSubmitting}
                      className="w-full bg-[#C9A227] hover:bg-[#A8871B] disabled:opacity-60 disabled:cursor-not-allowed text-[#0B1526] font-bold py-3"
                    >
                      {quoteSubmitting ? 'Sending…' : 'Request quote'}
                    </button>
                    <div className="text-xs text-[#5B6679] mt-2.5">
                      {cartCount} {cartCount === 1 ? 'part' : 'parts'} in your quote request
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-5 text-center">
                  <b className="block text-base mb-1.5 text-[#131F38]">Quote request sent</b>
                  <p className="text-sm text-[#5B6679] mb-4">
                    Reference <span className="font-mono">{quoteRef}</span>. A parts specialist replies with
                    pricing and lead time, usually within one business day.
                  </p>
                  <button
                    type="button"
                    onClick={resetQuote}
                    className="text-sm border border-[#D9DDE6] px-3.5 py-2 hover:border-[#131F38]"
                  >
                    Start another request
                  </button>
                </div>
              )}
            </div>

            <div className="bg-[#F4F5F8] border border-[#D9DDE6] p-4">
              <b className="block text-sm mb-1.5 text-[#131F38]">Can't find your part?</b>
              <p className="text-sm text-[#5B6679] mb-2.5">
                Send us a part number, drawing number, photo or sample component — our parts specialists can
                usually identify it and recommend replacements.
              </p>
              <a
                href="mailto:parts@choicebagging.com?subject=Part%20identification%20request"
                className="text-sm font-medium text-[#1A4FA0] hover:underline"
              >
                Email a photo or part number
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

export default EqidPartsStore;
